/**
 * Consent Constants
 *
 * Centralized file for all consent-related constants used across consent management components
 */

const { GRAY_300, WARNING_200, SUCCESS_500, ERROR_600, WARNING_500 } = require("./colors");

/**
 * SessionStorage key for highlighting consent events
 * Used when clicking eConsent links in notes/attachments
 */
export const HIGHLIGHT_CONSENT_EVENT_STORAGE_KEY = "highlightConsentEventId";

/**
 * Consent Action Types
 * Enum for consent action types used in consent forms
 */

export const CONSENT_RENDERING_TYPES = Object.freeze({
  CONSENT_RECORDED_MANUALLY: "consent_recorded_manually",
  CONSENT_RECEIVED_ELECTRONICALLY: "consent_received_electronically",
  LINK_SENT: "link_sent",
  LINK_REVOKED: "link_revoked",
  LINK_EXPIRED: "link_expired",
  CONSENT_EXPIRED: "consent_expired",
  CONSENT_REVOKED: "consent_revoked",
  NO_RECORD: "no_record"
});

/**
 * Consent Event Types
 * Backend event types received from the API
 * These represent the types of events stored in the backend consentEvent table
 */
export const CONSENT_EVENT_TYPES = {
  CONSENT_ACCEPTED: "consent_accepted",
  CONSENT_REVOKED: "consent_revoked",
  CONSENT_EXPIRED: "consent_expired",
  LINK_SENT: "link_sent",
  LINK_EXPIRED: "link_expired",
  LINK_REVOKED: "link_revoked"
};

/**
 * Consent Status Types
 * Current state of a patient's consent
 */
export const CONSENT_STATUS_TYPES = {
  NO_RECORD: "no_record",
  PENDING: "pending",
  RECEIVED: "received",
  REVOKED: "revoked",
  EXPIRED: "expired",
  LINK_EXPIRED: "link_expired"
};

/**
 * Consent Done By Types
 * Who performed the consent action
 */
export const CONSENT_DONE_BY_TYPES = {
  FINANCIAL_NAVIGATION: "financial_navigation",
  ECONSENT: "econsent",
  SYSTEM: "system"
};

/**
 * Consent Quick Action Keys
 * Enum for quick action keys used in consent management UI
 */
export const QUICK_ACTION_KEYS = {
  REQUEST: "request",
  REGISTER: "register",
  REVOKE: "revoke"
};

export const CONSENT_EVENT_VIA_ECONSENT_LINK = "econsent_link";

/**
 * Sentinel value for the `consent.expiration_months` site setting:
 * when set to 0, consent is valid indefinitely (no expiration date is stored).
 */
export const INDEFINITE_EXPIRATION_MONTHS = 0;

/**
 * Consent Via Options Configuration
 * Shared configuration for consent via options used in forms and display formatting
 */
export const CONSENT_VIA_SELECT_OPTIONS = [
  { value: "phone", label: "consent.forms.via_options.phone" },
  { value: "email", label: "consent.forms.via_options.email" },
  { value: "in_person", label: "consent.forms.via_options.in_person" },
  {
    value: "other",
    label: "consent.forms.via_options.other",
    historyTextDisplay: (t) => `(${t("consent.forms.via_options.other")})`
  }
];

export const CONSENT_ACTION_TYPES = {
  RECEIVED: "received",
  REVOKED: "revoked"
};

/**
 * Consent Error Codes
 * Error codes returned by the backend for consent-related errors
 */
export const CONSENT_ERROR_CODES = {
  FILE_UPLOAD_ERROR: "FILE_UPLOAD_ERROR"
};

/**
 * Consent Status Colors
 * Maps consent status types to their corresponding color values for UI display
 * Used for status dots in consent event items and status badges
 */
export const CONSENT_STATUS_COLORS = {
  [CONSENT_STATUS_TYPES.NO_RECORD]: GRAY_300,
  [CONSENT_STATUS_TYPES.PENDING]: WARNING_200,
  [CONSENT_STATUS_TYPES.RECEIVED]: SUCCESS_500,
  [CONSENT_STATUS_TYPES.REVOKED]: ERROR_600,
  [CONSENT_STATUS_TYPES.EXPIRED]: ERROR_600,
  [CONSENT_STATUS_TYPES.LINK_EXPIRED]: WARNING_500
};
